import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase, FUNCTIONS_URL } from '../lib/supabase';
import { toast } from 'sonner';
import {
  KeyRound, Plus, Copy, Check, AlertTriangle, Braces,
} from 'lucide-react';

const BASE_URL = `${FUNCTIONS_URL}/api`;

/* ------------------------------ key crypto helpers ------------------------------ */

const bytesToHex = (bytes) => Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');

const randomHex = (numBytes) => {
  const bytes = new Uint8Array(numBytes);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
};

const sha256Hex = async (text) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return bytesToHex(new Uint8Array(digest));
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/* --------------------------------- example data --------------------------------- */
// These mirror the deployed API's response shapes exactly (and DeveloperApiDocs.js).

const LESSON_ID = 'b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d';
const ISSUE_ID = '9f2c1b4e-6a1d-4c0f-8f9a-2e7d5b3a1c88';

const LESSON_LIST_ROW = {
  id: LESSON_ID,
  external_id: 'LSN-48213',
  tutor_name: 'James Whitfield',
  student_name: 'Amelia Ross',
  subject: 'GCSE Maths',
  lesson_timestamp: '2026-08-21T13:00:04Z',
  duration_minutes: 55,
  punctuality: { tutor_late_seconds: 0, student_late_seconds: 95 },
  quality: {
    overall_pct: 80,
    insufficient_evidence: false,
    ratings: {
      clarity: 'strong',
      engagement: 'adequate',
      adaptability: 'adequate',
      encouragement: 'strong',
      structure: 'adequate',
    },
  },
  issue_counts: { open: 1, total: 2 },
  links: {
    recording_url: 'https://recordings.lessonspace.com/lsn-48213',
    app_url: `https://app.tutortracer.com/lessons/${LESSON_ID}`,
  },
};

const FULL_ISSUE = {
  id: ISSUE_ID,
  session_id: LESSON_ID,
  category: 'safeguarding',
  severity: 8,
  status: 'open',
  state: 'open',
  care_level: 2,
  why_flagged: 'The tutor invited the student to move communication to a personal phone number, taking contact off the platform.',
  tutor_quote: 'Just message me on my personal number instead.',
  context: 'Discussing how to share extra practice papers near the end of the lesson.',
  filter_status: 'visible',
  filter_decision_reason: null,
  timestamp_display: '51:20',
  timestamp_seconds: 3080,
  playback_url: 'https://recordings.lessonspace.com/lsn-48213?t=3080',
  resolution_outcome: null,
  resolution_notes: null,
  resolved_at: null,
  resolved_by: null,
  actioned_at: null,
  actioned_by: null,
  breach_count: 1,
  last_breach_check_at: '2026-08-21T14:05:02Z',
  curriculum_check: {
    verdict: 'contradicts_curriculum',
    confidence: 0.92,
    summary: 'The safeguarding policy expressly prohibits moving communication with a student off the platform.',
    citations: [
      {
        document_title: 'Safeguarding Policy 2026',
        excerpt: 'Tutors must never move communication with a student to personal channels.',
        reasoning: 'Directly prohibits the behaviour in the flagged quote.',
      },
    ],
  },
  curriculum_checked_at: '2026-08-21T14:06:40Z',
  created_at: '2026-08-21T14:03:20Z',
  updated_at: '2026-08-21T14:06:40Z',
};

const TRANSCRIPT_LINES = [
  {
    timestamp: '00:12',
    seconds: 12,
    speaker: 'James Whitfield',
    role: 'tutor',
    text: 'Right, last week we finished expanding brackets — how did the homework go?',
  },
  {
    timestamp: '00:19',
    seconds: 19,
    speaker: 'Amelia Ross',
    role: 'student',
    text: 'Mostly fine, I got stuck on question five.',
  },
  {
    timestamp: '00:24',
    seconds: 24,
    speaker: 'James Whitfield',
    role: 'tutor',
    text: "No problem — let's start there and work through it together.",
  },
];

const LESSON_DETAIL = {
  ...LESSON_LIST_ROW,
  quality: {
    ...LESSON_LIST_ROW.quality,
    dimensions: [
      {
        key: 'clarity',
        label: 'Clarity',
        rating: 'strong',
        evidence: 'The box-method walkthrough was clearly structured and the student reproduced it unaided on the next question.',
        citations: [
          {
            quote: 'So each cell is one term multiplied out — try the next row yourself.',
            moment: 'Introducing the box method',
            timestamp_display: '12:40',
            timestamp_seconds: 760,
          },
        ],
      },
      {
        key: 'structure',
        label: 'Structure',
        rating: 'adequate',
        evidence: 'The lesson had a clear recap and practice arc, but the final section overran and homework was set hurriedly.',
        citations: [
          {
            quote: "We're nearly out of time, so quickly — questions one to five for next week.",
            moment: 'Setting homework in the last minute',
            timestamp_display: '53:40',
            timestamp_seconds: 3220,
          },
        ],
      },
    ],
    overall_summary: 'Clear, well-paced instruction with strong rapport; understanding checks were skipped in the middle third of the lesson.',
    coaching_suggestion: 'Pause after each worked example and ask the student to explain the step back before moving on.',
    insufficient_reason: null,
  },
  issues: [FULL_ISSUE],
  flagged_for_review: false,
  review_note: null,
  lesson_summary: "Quadratic factorisation lesson. The tutor recapped last week's homework, introduced the box method, and set five practice questions for next session.",
  transcript: {
    line_count: 597,
    lines: TRANSCRIPT_LINES,
  },
};

const PAGINATION_MORE = { next_cursor: 'eyJ2IjpbIjIwMjYtMDgtMjFUMTM6MDA6MDRaIl19', has_more: true, limit: 50 };
const PAGINATION_END = { next_cursor: null, has_more: false, limit: 50 };

/* --------------------------------- endpoints --------------------------------- */

const ENDPOINTS = [
  {
    key: 'lessons',
    label: 'Lessons (list)',
    path: '/v1/lessons',
    listShape: true,
    fields: [
      'id', 'external_id', 'tutor_name', 'student_name', 'subject', 'lesson_timestamp',
      'duration_minutes', 'punctuality', 'quality', 'issue_counts', 'links',
    ],
    example: {
      data: [LESSON_LIST_ROW],
      pagination: PAGINATION_MORE,
    },
    note: 'Need everything in one call? Add ?expand=full — every row then carries the complete Lesson detail shape (quality dimensions with evidence, all issues with their lifecycle state, summaries; limit capped at 20). Add include_transcript=true to inline every timestamped transcript line as well (limit capped at 5).',
  },
  {
    key: 'lesson_detail',
    label: 'Lesson detail',
    path: '/v1/lessons/{id}',
    listShape: false,
    fields: [
      'id', 'external_id', 'tutor_name', 'student_name', 'subject', 'lesson_timestamp',
      'duration_minutes', 'punctuality', 'quality', 'issue_counts', 'links',
      'issues', 'flagged_for_review', 'review_note', 'lesson_summary', 'transcript',
    ],
    note: 'The complete timestamped transcript is included by default; pass include_transcript=false to omit it.',
    caption: 'transcript.lines is truncated for display — the live response contains every timestamped line of the lesson.',
    example: LESSON_DETAIL,
  },
  {
    key: 'lesson_transcript',
    label: 'Lesson transcript',
    path: '/v1/lessons/{id}/transcript',
    listShape: false,
    fields: ['lesson', 'line_count', 'lines'],
    caption: 'lines is truncated for display — the live response contains every timestamped line of the lesson.',
    example: {
      lesson: { id: LESSON_ID, external_id: 'LSN-48213' },
      line_count: 597,
      lines: TRANSCRIPT_LINES,
    },
  },
  {
    key: 'issues',
    label: 'Issues',
    path: '/v1/issues',
    listShape: true,
    fields: [
      'id', 'session_id', 'category', 'severity', 'status', 'state', 'care_level',
      'why_flagged', 'tutor_quote', 'context', 'filter_status', 'filter_decision_reason',
      'timestamp_display', 'timestamp_seconds', 'playback_url',
      'resolution_outcome', 'resolution_notes', 'resolved_at', 'resolved_by',
      'actioned_at', 'actioned_by', 'breach_count', 'last_breach_check_at',
      'curriculum_check', 'curriculum_checked_at', 'created_at', 'updated_at', 'lesson',
    ],
    example: {
      data: [
        {
          ...FULL_ISSUE,
          lesson: {
            session_id: LESSON_ID,
            external_id: 'LSN-48213',
            tutor_name: 'James Whitfield',
            student_name: 'Amelia Ross',
            lesson_timestamp: '2026-08-21T13:00:04Z',
          },
        },
      ],
      pagination: PAGINATION_END,
    },
  },
  {
    key: 'tutors',
    label: 'Tutors',
    path: '/v1/tutors',
    listShape: true,
    fields: [
      'name', 'sessions', 'total_minutes', 'students', 'on_time_pct', 'quality', 'issues',
    ],
    example: {
      data: [
        {
          name: 'James Whitfield',
          sessions: 42,
          total_minutes: 2310,
          students: 9,
          on_time_pct: 93,
          quality: {
            overall_pct: 81,
            dimensions: {
              clarity: 84,
              engagement: 78,
              adaptability: 79,
              encouragement: 88,
              structure: 74,
            },
          },
          issues: { open: 1, total: 6 },
        },
      ],
      pagination: PAGINATION_END,
    },
  },
  {
    key: 'quality_summary',
    label: 'Quality summary',
    path: '/v1/quality/summary',
    listShape: false,
    fields: [
      'assessed_sessions', 'abstained_sessions', 'overall_pct',
      'distribution', 'dimensions', 'rubric_source',
    ],
    example: {
      assessed_sessions: 311,
      abstained_sessions: 9,
      overall_pct: 79,
      distribution: { strong_pct: 46, adequate_pct: 41, needs_development_pct: 13 },
      dimensions: [
        {
          key: 'clarity',
          label: 'Clarity',
          average_pct: 84,
          rated_sessions: 311,
          distribution: { strong_pct: 55, adequate_pct: 41, needs_development_pct: 4 },
        },
        {
          key: 'engagement',
          label: 'Engagement',
          average_pct: 76,
          rated_sessions: 311,
          distribution: { strong_pct: 40, adequate_pct: 47, needs_development_pct: 13 },
        },
        {
          key: 'structure',
          label: 'Structure',
          average_pct: 72,
          rated_sessions: 309,
          distribution: { strong_pct: 34, adequate_pct: 49, needs_development_pct: 17 },
        },
      ],
      rubric_source: 'built_in',
    },
  },
];

/* --------------------------------- helpers --------------------------------- */

// Keep only allowed top-level keys of a plain object; arrays/primitives pass through.
// Nested objects (punctuality, quality, links, …) toggle as one unit.
const pickFields = (obj, allowed) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  Object.keys(obj).forEach(k => { if (allowed.includes(k)) out[k] = obj[k]; });
  return out;
};

// Apply the checked-fields filter to a full response body: list responses get
// row-level filtering (the pagination envelope is kept); single objects get
// top-level filtering; error envelopes and non-objects pass through untouched.
const filterResponse = (body, allowed) => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  if (body.error) return body;
  if (Array.isArray(body.data)) {
    return { ...body, data: body.data.map(row => pickFields(row, allowed)) };
  }
  return pickFields(body, allowed);
};

const CopyButton = ({ text, isDark }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };
  return (
    <button
      onClick={copy}
      className={`p-1.5 rounded-md border flex-shrink-0 transition-colors ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'}`}
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

const CodeBlock = ({ code, isDark, maxHeight }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="relative group">
      <pre
        className={`p-3 rounded-lg text-[11px] leading-relaxed font-mono overflow-x-auto border ${isDark ? 'bg-[#0a0a0a] text-[#d4d4d8] border-[#27272a]' : 'bg-[#f5f5f5] text-[#333] border-[#e5e5e5]'}`}
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        {code}
      </pre>
      <button
        onClick={copy}
        title="Copy"
        className={`absolute top-2 right-2 p-1.5 rounded-md border opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'bg-[#18181b] border-[#27272a] text-[#a1a1aa] hover:text-white' : 'bg-white border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a]'}`}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
};

/* ---------------------------------- page ---------------------------------- */

const ApiWorkspace = () => {
  const { user, organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // --- keys ---
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // { name, fullKey }

  // --- response builder ---
  const [endpointKey, setEndpointKey] = useState('lesson_detail');
  const [checkedByEndpoint, setCheckedByEndpoint] = useState({}); // key -> array of field names

  const endpoint = ENDPOINTS.find(ep => ep.key === endpointKey) || ENDPOINTS[0];
  const checkedFields = checkedByEndpoint[endpoint.key] ?? endpoint.fields;

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const mutedText = isDark ? 'text-[#a1a1aa]' : 'text-[#666]';
  const faintText = isDark ? 'text-[#52525b]' : 'text-[#999]';
  const strongText = isDark ? 'text-white' : 'text-[#1a1a1a]';
  const accent = isDark ? 'text-[#7662ac]' : 'text-[#586f8d]';
  const inputClass = `w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`;
  const primaryBtn = `text-xs px-3 py-1.5 rounded-md font-medium ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'} disabled:opacity-50`;
  const secondaryBtn = `text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'} disabled:opacity-50`;
  const iconBox = `w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`;

  /* ------------------------------ data loading ------------------------------ */

  const loadKeys = useCallback(async () => {
    if (!organizationId) return;
    setKeysLoading(true);
    try {
      const { data, error } = await supabase.from('api_keys')
        .select('id, name, key_prefix, created_at, last_used_at, revoked_at')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setKeys(data || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load API keys');
    }
    setKeysLoading(false);
  }, [organizationId]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  /* ------------------------------- key actions ------------------------------- */

  const createKey = async (e) => {
    e.preventDefault();
    const name = keyName.trim();
    if (!name) { toast.error('Give the key a name'); return; }
    if (!organizationId) { toast.error('No organization loaded yet'); return; }
    setCreatingKey(true);
    try {
      const fullKey = `tt_live_${randomHex(32)}`;
      const keyHash = await sha256Hex(fullKey);
      const { error } = await supabase.from('api_keys').insert({
        organization_id: organizationId,
        name,
        key_prefix: fullKey.slice(0, 12),
        key_hash: keyHash,
        created_by: user?.id,
      });
      if (error) throw error;
      setRevealedKey({ name, fullKey });
      setKeyName('');
      setShowKeyForm(false);
      await loadKeys();
    } catch (err) {
      toast.error(err.message || 'Failed to create key');
    }
    setCreatingKey(false);
  };

  const revokeKey = async (key) => {
    const ok = window.confirm(`Revoke API key "${key.name}"?\n\nAny integration using this key will immediately stop working. This cannot be undone.`);
    if (!ok) return;
    try {
      const { error } = await supabase.from('api_keys')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', key.id)
        .eq('organization_id', organizationId);
      if (error) throw error;
      toast.success('Key revoked');
      await loadKeys();
    } catch (e) {
      toast.error(e.message || 'Failed to revoke key');
    }
  };

  /* --------------------------- response builder --------------------------- */

  const toggleField = (field) => {
    setCheckedByEndpoint(prev => {
      const current = prev[endpoint.key] ?? endpoint.fields;
      const next = current.includes(field)
        ? current.filter(f => f !== field)
        : endpoint.fields.filter(f => current.includes(f) || f === field); // keep original field order
      return { ...prev, [endpoint.key]: next };
    });
  };

  const templateJson = useMemo(
    () => JSON.stringify(filterResponse(endpoint.example, checkedFields), null, 2),
    [endpoint, checkedFields]
  );

  const curlPath = endpoint.path.replace('{id}', '{lesson_id}');
  const curlSnippet = `curl "${BASE_URL}${curlPath}" \\\n  -H "Authorization: Bearer <your key>"`;

  /* ---------------------------------- render ---------------------------------- */

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${strongText}`}>API</h1>
        <p className={`text-sm mt-1 ${mutedText}`}>Feed structured TutorTracer data into your own systems.</p>
      </div>

      {/* ===================== 1. API KEYS ===================== */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={iconBox}>
            <KeyRound className={`w-5 h-5 ${accent}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h2 className={`text-sm font-medium mb-1 ${strongText}`}>API Keys</h2>
          </div>
          <button onClick={() => { setShowKeyForm(v => !v); setRevealedKey(null); }} className={primaryBtn}>
            <span className="flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Create key</span>
          </button>
        </div>

        {showKeyForm && (
          <form onSubmit={createKey} className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
            <label className={`block text-xs font-medium mb-1.5 ${strongText}`}>Key name</label>
            <div className="flex gap-2">
              <input
                value={keyName}
                onChange={e => setKeyName(e.target.value)}
                placeholder="e.g. Data warehouse sync"
                className={inputClass}
                autoFocus
              />
              <button type="submit" disabled={creatingKey || !keyName.trim()} className={primaryBtn}>
                {creatingKey ? 'Creating...' : 'Generate'}
              </button>
              <button type="button" onClick={() => setShowKeyForm(false)} className={secondaryBtn}>Cancel</button>
            </div>
          </form>
        )}

        {revealedKey && (
          <div className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-amber-400/5 border-amber-400/20' : 'bg-amber-50 border-amber-200'}`}>
            <div className={`flex items-center gap-2 text-xs font-bold mb-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              Copy this key now — it will never be shown again.
            </div>
            <p className={`text-xs mb-2 ${mutedText}`}>Key "{revealedKey.name}" was created. Store it somewhere safe, such as a secrets manager.</p>
            <div className="flex items-center gap-2">
              <code className={`flex-1 px-3 py-2 rounded-md text-xs font-mono break-all ${isDark ? 'bg-[#0a0a0a] text-white border border-[#27272a]' : 'bg-white text-[#1a1a1a] border border-[#e5e5e5]'}`}>
                {revealedKey.fullKey}
              </code>
              <CopyButton text={revealedKey.fullKey} isDark={isDark} />
            </div>
            <button onClick={() => setRevealedKey(null)} className={`mt-2 text-[11px] underline ${mutedText}`}>
              I've saved it — dismiss
            </button>
          </div>
        )}

        {keysLoading ? (
          <p className={`text-xs ${mutedText}`}>Loading...</p>
        ) : keys.length === 0 ? (
          <p className={`text-xs ${faintText}`}>No API keys yet. Create one to start using the API.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className={`text-[10px] uppercase tracking-wide ${faintText}`}>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Key</th>
                  <th className="pb-2 pr-4 font-medium">Created</th>
                  <th className="pb-2 pr-4 font-medium">Last used</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => {
                  const revoked = !!key.revoked_at;
                  return (
                    <tr key={key.id} className={`border-t text-xs ${isDark ? 'border-[#27272a]' : 'border-[#f0f0f0]'} ${revoked ? 'opacity-45' : ''}`}>
                      <td className={`py-2.5 pr-4 font-medium ${strongText}`}>{key.name}</td>
                      <td className={`py-2.5 pr-4 font-mono ${mutedText}`}>{key.key_prefix}&hellip;</td>
                      <td className={`py-2.5 pr-4 ${mutedText}`}>{formatDate(key.created_at)}</td>
                      <td className={`py-2.5 pr-4 ${mutedText}`}>{formatDate(key.last_used_at) || 'never'}</td>
                      <td className="py-2.5 pr-4">
                        {revoked ? (
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${isDark ? 'bg-red-400/10 text-red-400 border-red-400/20' : 'bg-red-50 text-red-700 border-red-200'}`}>Revoked</span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${isDark ? 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>Active</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right">
                        {!revoked && (
                          <button onClick={() => revokeKey(key)} className={`text-[11px] px-2 py-1 rounded-md ${isDark ? 'text-red-400 hover:bg-red-400/10' : 'text-red-600 hover:bg-red-50'}`}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===================== 2. RESPONSE BUILDER ===================== */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={iconBox}>
            <Braces className={`w-5 h-5 ${accent}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h2 className={`text-sm font-medium mb-1 ${strongText}`}>Response Builder</h2>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {/* left: endpoint + fields */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${strongText}`}>Endpoint</label>
            <select
              value={endpointKey}
              onChange={e => setEndpointKey(e.target.value)}
              className={inputClass}
            >
              {ENDPOINTS.map(ep => (
                <option key={ep.key} value={ep.key}>{ep.label} — {ep.path}</option>
              ))}
            </select>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium ${strongText}`}>Included in the format</span>
                <button
                  onClick={() => setCheckedByEndpoint(prev => ({ ...prev, [endpoint.key]: endpoint.fields }))}
                  className={`text-[11px] underline ${mutedText}`}
                >
                  Select all
                </button>
              </div>
              <div className="space-y-1.5">
                {endpoint.fields.map(field => (
                  <label key={field} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkedFields.includes(field)}
                      onChange={() => toggleField(field)}
                    />
                    <span className={`text-xs font-mono ${strongText}`}>{field}</span>
                  </label>
                ))}
              </div>
              <p className={`text-[11px] mt-2 ${faintText}`}>
                Nested objects (e.g. <code className="font-mono">quality</code>, <code className="font-mono">punctuality</code>) toggle as one unit.
              </p>
              {endpoint.listShape && (
                <p className={`text-[11px] mt-2 ${faintText}`}>
                  List endpoints wrap rows in a <code className="font-mono">data</code> array; the fields above apply to each row. A <code className="font-mono">pagination</code> object with <code className="font-mono">next_cursor</code>, <code className="font-mono">has_more</code> and <code className="font-mono">limit</code> is always present.
                </p>
              )}
              {endpoint.note && (
                <p className={`text-[11px] mt-2 ${mutedText}`}>{endpoint.note}</p>
              )}
              {checkedFields.length === 0 && (
                <p className={`text-[11px] mt-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  Nothing selected — responses will be empty objects.
                </p>
              )}
            </div>
          </div>

          {/* right: template + curl */}
          <div className="min-w-0">
            <h3 className={`text-[10px] uppercase tracking-wide mb-1.5 ${faintText}`}>Template</h3>
            <CodeBlock code={templateJson} isDark={isDark} maxHeight="360px" />
            {endpoint.caption && (
              <p className={`text-[11px] mt-1.5 ${faintText}`}>{endpoint.caption}</p>
            )}
            <h3 className={`text-[10px] uppercase tracking-wide mt-3 mb-1.5 ${faintText}`}>Request</h3>
            <CodeBlock code={curlSnippet} isDark={isDark} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiWorkspace;
